import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Custom hook for managing multilingual text
 * Automatically fetches and caches translations based on selected language
 */
export const useMultilingualText = (textKeys = []) => {
  const { selectedLanguage, getMultilingualText } = useLanguage();
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch translations when language or keys change
  const fetchTranslations = useCallback(async () => {
    if (!textKeys || textKeys.length === 0) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const translatedTexts = await getMultilingualText(textKeys);
      setTranslations(translatedTexts);
    } catch (err) {
      console.error('[useMultilingualText] Error fetching translations:', err);
      setError(err.message);
      // Set fallback translations using the keys as values
      const fallbackTranslations = {};
      textKeys.forEach(key => {
        fallbackTranslations[key] = key; // Use key as fallback text
      });
      setTranslations(fallbackTranslations);
    } finally {
      setIsLoading(false);
    }
  }, [textKeys, getMultilingualText]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  // Refetch translations when language changes
  useEffect(() => {
    fetchTranslations();
  }, [selectedLanguage?.code, fetchTranslations]);

  /**
   * Get translated text for a specific key
   * @param {string} key - Text key to translate
   * @param {string} fallback - Fallback text if translation not found
   * @returns {string} Translated text or fallback
   */
  const getText = useCallback((key, fallback = key) => {
    return translations[key] || fallback;
  }, [translations]);

  /**
   * Get multiple translated texts
   * @param {string[]} keys - Array of text keys
   * @returns {Object} Object with key-value pairs of translations
   */
  const getTexts = useCallback((keys) => {
    const result = {};
    keys.forEach(key => {
      result[key] = translations[key] || key;
    });
    return result;
  }, [translations]);

  return {
    translations,
    isLoading,
    error,
    getText,
    getTexts,
    selectedLanguage,
    refetch: fetchTranslations,
  };
};

/**
 * Default text keys for common UI elements
 */
export const COMMON_TEXT_KEYS = {
  // Navigation
  MY_APPLICATIONS: 'my_applications',
  MY_REFERRALS: 'my_referrals', 
  MY_EARNINGS: 'my_earnings',
  PROFILE: 'profile',
  
  // Actions
  APPLY_NOW: 'apply_now',
  VIEW_DETAILS: 'view_details',
  REFER_EARN: 'refer_earn',
  SIGN_IN: 'sign_in',
  SIGN_OUT: 'sign_out',
  REFRESH: 'refresh',
  SAVE: 'save',
  CANCEL: 'cancel',
  SUBMIT: 'submit',
  
  // Status
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success',
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  
  // Referrals
  TOTAL_REFERRALS: 'total_referrals',
  APPLICATIONS_SUBMITTED: 'applications_submitted',
  CANDIDATES_JOINED: 'candidates_joined',
  COMPLETED_ONE_MONTH: 'completed_one_month',
  FILTER_REFERRALS: 'filter_referrals',
  YOUR_REFERRALS: 'your_referrals',
  NO_REFERRALS: 'no_referrals',
  
  // Earnings
  TOTAL_EARNINGS: 'total_earnings',
  AVAILABLE_BALANCE: 'available_balance',
  PENDING_AMOUNT: 'pending_amount',
  TOTAL_WITHDRAWN: 'total_withdrawn',
  TRANSACTION_HISTORY: 'transaction_history',
  NO_TRANSACTIONS: 'no_transactions',
  
  // Job related
  JOB_TITLE: 'job_title',
  COMPANY: 'company',
  LOCATION: 'location',
  SALARY: 'salary',
  EXPERIENCE: 'experience',
  SKILLS: 'skills',
  JOB_DESCRIPTION: 'job_description',
  
  // Forms
  FULL_NAME: 'full_name',
  EMAIL: 'email',
  MOBILE: 'mobile',
  RESUME: 'resume',
  UPLOAD_RESUME: 'upload_resume',
  
  // Messages
  WELCOME_MESSAGE: 'welcome_message',
  ERROR_LOADING_DATA: 'error_loading_data',
  NO_DATA_AVAILABLE: 'no_data_available',
  TRY_AGAIN: 'try_again',
};

export default useMultilingualText;
